import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth } from "date-fns";
import {
    CalendarDays,
    Inbox,
    MessageSquare,
    LogOut,
    ChevronLeft,
    ChevronRight,
    Check,
    X as XIcon,
    Trash2,
    BarChart3,
    Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_LABELS = {
    new: "New",
    contacted: "Contacted",
    confirmed: "Confirmed",
    declined: "Declined",
};

const STATUS_STYLES = {
    new: "bg-[hsl(43_65%_52%/0.18)] text-[hsl(28_50%_30%)]",
    contacted: "bg-[hsl(200_30%_50%/0.18)] text-[hsl(200_50%_25%)]",
    confirmed: "bg-[hsl(137_40%_38%/0.18)] text-[hsl(137_50%_25%)]",
    declined: "bg-[hsl(0_60%_50%/0.15)] text-[hsl(0_60%_30%)]",
};

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [inquiries, setInquiries] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingInquiry, setEditingInquiry] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [a, b, s] = await Promise.all([
                api.get("/admin/inquiries"),
                api.get("/admin/chat-sessions"),
                api.get("/admin/stats"),
            ]);
            setInquiries(a.data);
            setSessions(b.data);
            setStats(s.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onSignOut = async () => {
        await logout();
        navigate("/admin/login");
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/inquiries/${id}`, { status });
            toast.success(`Marked ${STATUS_LABELS[status].toLowerCase()}.`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const removeInquiry = async (id) => {
        if (!window.confirm("Delete this inquiry?")) return;
        try {
            await api.delete(`/admin/inquiries/${id}`);
            toast.success("Deleted.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveInquiry = async (id, updates) => {
        try {
            await api.patch(`/admin/inquiries/${id}`, updates);
            toast.success("Inquiry updated.");
            setEditingInquiry(null);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--muted))]" data-testid="admin-dashboard">
            <header className="bg-background border-b border-border">
                <div className="container-px h-20 flex items-center justify-between">
                    <Link to="/" className="flex flex-col leading-tight" data-testid="dashboard-brand">
                        <span className="font-serif text-2xl tracking-tight">López Hill</span>
                        <span className="overline text-[10px]">Team portal</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:inline">
                            {user?.email}
                        </span>
                        <button
                            onClick={onSignOut}
                            className="flex items-center gap-2 px-4 py-2 border border-border text-sm hover:bg-[hsl(var(--muted))] transition-colors"
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={1.4} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="container-px py-10 sm:py-14">
                <p className="overline">Dashboard</p>
                <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight">
                    Good {timeOfDay()}, {user?.name?.split(" ")[0] || "Admin"}.
                </h1>

                <Stats stats={stats} loading={loading} />

                <Tabs defaultValue="leads" className="mt-12">
                    <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start gap-2 p-0 h-auto">
                        <TabTrig value="leads" icon={Inbox} label="Inquiries & Leads" testId="tab-leads" />
                        <TabTrig value="calendar" icon={CalendarDays} label="Calendar" testId="tab-calendar" />
                        <TabTrig value="chats" icon={MessageSquare} label="Chat Logs" testId="tab-chats" />
                    </TabsList>

                    <TabsContent value="leads" className="mt-10">
                        <LeadsTable
                            inquiries={inquiries}
                            onStatus={updateStatus}
                            onDelete={removeInquiry}
                            onEdit={setEditingInquiry}
                            loading={loading}
                        />
                    </TabsContent>

                    <TabsContent value="calendar" className="mt-10">
                        <CalendarView inquiries={inquiries} onEdit={setEditingInquiry} />
                    </TabsContent>

                    <TabsContent value="chats" className="mt-10">
                        <ChatLogs sessions={sessions} loading={loading} />
                    </TabsContent>
                </Tabs>
            </main>

            <EditInquiryDialog
                inquiry={editingInquiry}
                onClose={() => setEditingInquiry(null)}
                onSave={saveInquiry}
            />
        </div>
    );
}

function timeOfDay() {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 18) return "afternoon";
    return "evening";
}

function Stats({ stats, loading }) {
    const items = [
        { k: "total_inquiries", l: "Total Inquiries" },
        { k: "new_inquiries", l: "New" },
        { k: "confirmed_bookings", l: "Confirmed" },
        { k: "chat_sessions", l: "Chat Sessions" },
    ];
    return (
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {items.map((i) => (
                <div key={i.k} className="bg-background p-6 sm:p-8" data-testid={`stat-${i.k}`}>
                    <div className="overline flex items-center gap-2">
                        <BarChart3 className="h-3 w-3" strokeWidth={1.5} /> {i.l}
                    </div>
                    <div className="mt-3 font-serif text-4xl">
                        {loading ? "—" : stats?.[i.k] ?? 0}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TabTrig({ value, icon: Icon, label, testId }) {
    return (
        <TabsTrigger
            value={value}
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--primary))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm tracking-wide font-medium flex items-center gap-2"
            data-testid={testId}
        >
            <Icon className="h-4 w-4" strokeWidth={1.4} /> {label}
        </TabsTrigger>
    );
}

function LeadsTable({ inquiries, onStatus, onDelete, onEdit, loading }) {
    if (loading) return <div className="text-muted-foreground">Loading…</div>;
    if (!inquiries.length)
        return (
            <div className="border border-border bg-background p-16 text-center text-muted-foreground" data-testid="leads-empty">
                No inquiries yet. Submissions from the form and the chatbot will appear here.
            </div>
        );

    return (
        <div className="border border-border bg-background overflow-x-auto" data-testid="leads-table">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Couple / Host</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {inquiries.map((i) => (
                        <TableRow key={i.id} data-testid={`lead-row-${i.id}`}>
                            <TableCell>
                                <div className="font-medium">{i.couple_names}</div>
                                <div className="text-xs text-muted-foreground">
                                    {format(parseISO(i.created_at), "MMM d, yyyy")}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div>{i.email}</div>
                                {i.phone && <div className="text-xs text-muted-foreground">{i.phone}</div>}
                            </TableCell>
                            <TableCell>
                                <div>{i.event_type}</div>
                                <div className="text-xs text-muted-foreground">
                                    {i.event_date ? format(parseISO(i.event_date), "MMM d, yyyy") : "Date TBD"}
                                    {i.guest_count ? ` · ${i.guest_count} guests` : ""}
                                </div>
                            </TableCell>
                            <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">
                                {i.source}
                            </TableCell>
                            <TableCell>
                                <Select
                                    value={i.status}
                                    onValueChange={(v) => onStatus(i.id, v)}
                                >
                                    <SelectTrigger
                                        className={cn(
                                            "h-8 w-32 border-0 rounded-none text-xs uppercase tracking-wider",
                                            STATUS_STYLES[i.status],
                                        )}
                                        data-testid={`status-select-${i.id}`}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>
                                                {v}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="inline-flex gap-1">
                                    <IconBtn
                                        onClick={() => onEdit(i)}
                                        title="Edit"
                                        testId={`edit-${i.id}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </IconBtn>
                                    <IconBtn
                                        onClick={() => onStatus(i.id, "confirmed")}
                                        title="Confirm"
                                        testId={`confirm-${i.id}`}
                                    >
                                        <Check className="h-4 w-4" />
                                    </IconBtn>
                                    <IconBtn
                                        onClick={() => onStatus(i.id, "declined")}
                                        title="Decline"
                                        testId={`decline-${i.id}`}
                                    >
                                        <XIcon className="h-4 w-4" />
                                    </IconBtn>
                                    <IconBtn
                                        onClick={() => onDelete(i.id)}
                                        title="Delete"
                                        testId={`delete-${i.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </IconBtn>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function IconBtn({ children, onClick, title, testId }) {
    return (
        <button
            onClick={onClick}
            title={title}
            className="h-8 w-8 inline-flex items-center justify-center border border-border hover:bg-[hsl(var(--muted))] transition-colors"
            data-testid={testId}
        >
            {children}
        </button>
    );
}

function CalendarView({ inquiries, onEdit }) {
    const scheduled = useMemo(
        () => inquiries.filter((i) => i.event_date),
        [inquiries],
    );
    const unscheduled = useMemo(
        () => inquiries.filter((i) => !i.event_date),
        [inquiries],
    );

    const initialMonth = useMemo(() => {
        const today = startOfMonth(new Date());
        if (!scheduled.length) return today;
        const upcoming = scheduled
            .map((i) => parseISO(i.event_date))
            .filter((d) => d >= today)
            .sort((a, b) => a - b);
        if (upcoming.length) return startOfMonth(upcoming[0]);
        // No upcoming events — fall back to most recent past event month
        const past = scheduled
            .map((i) => parseISO(i.event_date))
            .sort((a, b) => b - a);
        return startOfMonth(past[0]);
    }, [scheduled]);

    const [month, setMonth] = useState(initialMonth);

    // Re-sync when inquiries load asynchronously
    useEffect(() => {
        setMonth(initialMonth);
    }, [initialMonth]);

    const events = useMemo(() => {
        const map = new Map();
        scheduled.forEach((i) => {
            const d = format(parseISO(i.event_date), "yyyy-MM-dd");
            if (!map.has(d)) map.set(d, []);
            map.get(d).push(i);
        });
        return map;
    }, [scheduled]);

    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    const days = [];
    let cursor = start;
    while (cursor <= end) {
        days.push(cursor);
        cursor = addDays(cursor, 1);
    }

    return (
        <div className="space-y-8" data-testid="calendar-view-wrapper">
        <div className="bg-background border border-border" data-testid="calendar-view">
            <div className="flex flex-wrap items-center justify-between gap-3 p-6 border-b border-border">
                <div>
                    <h2 className="font-serif text-2xl tracking-tight">
                        {format(month, "MMMM yyyy")}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="calendar-summary">
                        {scheduled.length} scheduled · {unscheduled.length} awaiting a date
                    </p>
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => setMonth(addDays(startOfMonth(month), -1))}
                        className="h-9 w-9 border border-border flex items-center justify-center hover:bg-[hsl(var(--muted))]"
                        data-testid="cal-prev"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => setMonth(startOfMonth(new Date()))}
                        className="px-3 h-9 border border-border text-xs uppercase tracking-wider hover:bg-[hsl(var(--muted))]"
                        data-testid="cal-today"
                    >
                        Today
                    </button>
                    <button
                        onClick={() => setMonth(addDays(endOfMonth(month), 1))}
                        className="h-9 w-9 border border-border flex items-center justify-center hover:bg-[hsl(var(--muted))]"
                        data-testid="cal-next"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="p-3 border-r last:border-r-0 border-border">
                        {d}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7">
                {days.map((d) => {
                    const key = format(d, "yyyy-MM-dd");
                    const ev = events.get(key) || [];
                    const isToday = isSameDay(d, new Date());
                    const inMonth = isSameMonth(d, month);
                    return (
                        <div
                            key={key}
                            className={cn(
                                "min-h-[110px] p-2 border-r border-b border-border last:border-r-0",
                                !inMonth && "bg-[hsl(var(--muted))/.4] text-muted-foreground/60",
                            )}
                            data-testid={`cal-day-${key}`}
                        >
                            <div className={cn("text-xs", isToday && "text-[hsl(var(--primary))] font-medium")}>
                                {format(d, "d")}
                            </div>
                            <div className="mt-1 space-y-1">
                                {ev.map((e) => (
                                    <button
                                        key={e.id}
                                        type="button"
                                        onClick={() => onEdit?.(e)}
                                        className={cn(
                                            "block w-full text-left text-[10px] px-1.5 py-1 truncate hover:opacity-80 transition-opacity cursor-pointer",
                                            e.status === "confirmed" &&
                                                "bg-[hsl(137_40%_38%/0.2)] text-[hsl(137_50%_22%)]",
                                            e.status === "contacted" &&
                                                "bg-[hsl(200_30%_50%/0.2)] text-[hsl(200_50%_22%)]",
                                            e.status === "new" &&
                                                "bg-[hsl(43_65%_52%/0.25)] text-[hsl(28_50%_28%)]",
                                            e.status === "declined" &&
                                                "bg-[hsl(0_60%_50%/0.15)] text-[hsl(0_60%_30%)] line-through",
                                        )}
                                        title={`${e.couple_names} · ${e.event_type} · ${e.status} — click to edit`}
                                        data-testid={`cal-event-${e.id}`}
                                    >
                                        {e.couple_names}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>

        {unscheduled.length > 0 && (
            <div className="bg-background border border-border" data-testid="unscheduled-list">
                <div className="p-6 border-b border-border">
                    <h3 className="font-serif text-xl tracking-tight">Awaiting a date</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Inquiries (including chatbot leads) where the couple hasn't picked a date yet. Follow up to lock it in and place them on the calendar.
                    </p>
                </div>
                <ul className="divide-y divide-border">
                    {unscheduled.map((i) => (
                        <li
                            key={i.id}
                            className="p-4 sm:p-6 flex flex-wrap items-center justify-between gap-3"
                            data-testid={`unscheduled-${i.id}`}
                        >
                            <div className="min-w-0">
                                <div className="font-medium truncate">{i.couple_names}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {i.event_type} · {i.email}
                                    {i.phone ? ` · ${i.phone}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                    {i.source}
                                </span>
                                <span
                                    className={cn(
                                        "text-[10px] uppercase tracking-[0.18em] px-2 py-1",
                                        STATUS_STYLES[i.status],
                                    )}
                                >
                                    {STATUS_LABELS[i.status]}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onEdit?.(i)}
                                    className="h-8 px-3 inline-flex items-center gap-2 border border-border text-xs uppercase tracking-wider hover:bg-[hsl(var(--muted))]"
                                    data-testid={`unscheduled-edit-${i.id}`}
                                >
                                    <Pencil className="h-3.5 w-3.5" /> Set date
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        )}
        </div>
    );
}

function ChatLogs({ sessions, loading }) {
    const [active, setActive] = useState(null);
    if (loading) return <div className="text-muted-foreground">Loading…</div>;
    if (!sessions.length)
        return (
            <div className="border border-border bg-background p-16 text-center text-muted-foreground" data-testid="chats-empty">
                No chat sessions yet.
            </div>
        );

    return (
        <div className="grid lg:grid-cols-12 gap-px bg-border border border-border" data-testid="chat-logs">
            <ul className="lg:col-span-4 bg-background max-h-[640px] overflow-y-auto">
                {sessions.map((s) => (
                    <li key={s.session_id}>
                        <button
                            onClick={() => setActive(s)}
                            className={cn(
                                "w-full text-left p-4 border-b border-border hover:bg-[hsl(var(--muted))]",
                                active?.session_id === s.session_id && "bg-[hsl(var(--muted))]",
                            )}
                            data-testid={`session-${s.session_id}`}
                        >
                            <div className="font-medium text-sm truncate">
                                {s.visitor_name || `Session ${s.session_id.slice(0, 8)}`}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {format(parseISO(s.last_activity || s.created_at), "MMM d, h:mm a")}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {(s.messages || []).length} messages
                            </div>
                        </button>
                    </li>
                ))}
            </ul>

            <div className="lg:col-span-8 bg-background p-6 max-h-[640px] overflow-y-auto">
                {!active && (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                        Select a session to review the conversation.
                    </div>
                )}
                {active && (
                    <div className="space-y-3" data-testid="chat-conversation">
                        {(active.messages || []).map((m, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "max-w-[80%] px-4 py-3 text-sm leading-relaxed",
                                    m.role === "user"
                                        ? "ml-auto bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                        : "bg-[hsl(var(--muted))]",
                                )}
                            >
                                {m.text}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


const EVENT_TYPES = ["Wedding", "Tequila Tasting", "Private Event", "Corporate", "Other"];

function EditInquiryDialog({ inquiry, onClose, onSave }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (inquiry) {
            setForm({
                couple_names: inquiry.couple_names || "",
                email: inquiry.email || "",
                phone: inquiry.phone || "",
                event_type: inquiry.event_type || "Wedding",
                event_date: inquiry.event_date || "",
                guest_count: inquiry.guest_count ?? "",
                status: inquiry.status || "new",
                notes: inquiry.notes || "",
                message: inquiry.message || "",
            });
        } else {
            setForm(null);
        }
    }, [inquiry]);

    const open = Boolean(inquiry && form);

    const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!inquiry || !form) return;
        const payload = {
            couple_names: form.couple_names.trim(),
            email: form.email.trim().toLowerCase(),
            phone: form.phone.trim() || null,
            event_type: form.event_type,
            event_date: form.event_date || null,
            guest_count: form.guest_count === "" ? null : Number(form.guest_count),
            status: form.status,
            notes: form.notes.trim() || null,
            message: form.message.trim() || null,
        };
        setSaving(true);
        try {
            await onSave(inquiry.id, payload);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl bg-background" data-testid="edit-inquiry-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl tracking-tight">
                        Edit inquiry
                    </DialogTitle>
                    <DialogDescription>
                        Update the couple's details, status or event date. Saved changes will
                        appear immediately in the calendar.
                    </DialogDescription>
                </DialogHeader>

                {form && (
                    <form onSubmit={submit} className="space-y-4 mt-2" data-testid="edit-inquiry-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Couple / Host">
                                <Input
                                    value={form.couple_names}
                                    onChange={(e) => update("couple_names", e.target.value)}
                                    required
                                    data-testid="edit-couple-names"
                                />
                            </Field>
                            <Field label="Email">
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    required
                                    data-testid="edit-email"
                                />
                            </Field>
                            <Field label="Phone">
                                <Input
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    data-testid="edit-phone"
                                />
                            </Field>
                            <Field label="Event type">
                                <Select
                                    value={form.event_type}
                                    onValueChange={(v) => update("event_type", v)}
                                >
                                    <SelectTrigger data-testid="edit-event-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EVENT_TYPES.map((t) => (
                                            <SelectItem key={t} value={t}>
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Event date">
                                <Input
                                    type="date"
                                    value={form.event_date}
                                    onChange={(e) => update("event_date", e.target.value)}
                                    data-testid="edit-event-date"
                                />
                            </Field>
                            <Field label="Guests">
                                <Input
                                    type="number"
                                    min="1"
                                    max="2000"
                                    value={form.guest_count}
                                    onChange={(e) => update("guest_count", e.target.value)}
                                    data-testid="edit-guest-count"
                                />
                            </Field>
                            <Field label="Status">
                                <Select
                                    value={form.status}
                                    onValueChange={(v) => update("status", v)}
                                >
                                    <SelectTrigger data-testid="edit-status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>
                                                {v}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <Field label="Internal notes">
                            <Textarea
                                value={form.notes}
                                onChange={(e) => update("notes", e.target.value)}
                                rows={3}
                                placeholder="Follow-up plan, special requests, deposit status…"
                                data-testid="edit-notes"
                            />
                        </Field>

                        <Field label="Original message">
                            <Textarea
                                value={form.message}
                                onChange={(e) => update("message", e.target.value)}
                                rows={3}
                                data-testid="edit-message"
                            />
                        </Field>

                        <DialogFooter className="mt-6 gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2 border border-border text-sm hover:bg-[hsl(var(--muted))]"
                                data-testid="edit-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-5 py-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm tracking-wide disabled:opacity-60"
                                data-testid="edit-save"
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="overline text-[10px]">{label}</Label>
            {children}
        </div>
    );
}
